import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  HelpCircle,
  ChevronDown,
  ChevronUp
} from 'lucide-react'
import { faqService, FAQ, CreateFAQData, UpdateFAQData } from '../services/faqService'
import toast from 'react-hot-toast'

const FAQs = () => {
  const [faqs, setFaqs] = useState<FAQ[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('ALL')
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())
  const [selectedFAQ, setSelectedFAQ] = useState<FAQ | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState<CreateFAQData>({
    question: '',
    answer: '',
    category: '',
    order: 0,
    isActive: true
  })

  useEffect(() => {
    fetchFAQs()
  }, [])

  const fetchFAQs = async () => {
    try {
      setLoading(true)
      const response = await faqService.getFAQs({ limit: 100 })
      
      // Handle both paginated and non-paginated responses
      if (response.success) {
        if (Array.isArray(response.data)) {
          setFaqs(response.data)
        } else if (response.data && 'faqs' in response.data) {
          setFaqs(response.data.faqs)
        }
      }
    } catch (error: any) {
      console.error('Error fetching FAQs:', error)
      toast.error(error.message || 'Failed to fetch FAQs')
    } finally {
      setLoading(false)
    }
  }

  const categories = ['ALL', ...Array.from(new Set(faqs.map(faq => faq.category).filter(Boolean)))]

  const filteredFAQs = faqs.filter(faq => {
    const matchesSearch = 
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesCategory = categoryFilter === 'ALL' || faq.category === categoryFilter

    return matchesSearch && matchesCategory
  })

  const toggleExpanded = (faqId: string) => {
    const newExpanded = new Set(expandedItems)
    if (newExpanded.has(faqId)) {
      newExpanded.delete(faqId)
    } else {
      newExpanded.add(faqId)
    }
    setExpandedItems(newExpanded)
  }

  const handleFAQAction = (action: string, faq?: FAQ) => {
    if (action === 'create') {
      setSelectedFAQ(null)
      setIsEditing(false)
      setFormData({
        question: '',
        answer: '',
        category: '',
        order: 0,
        isActive: true
      })
    } else if (faq) {
      setSelectedFAQ(faq)
      setIsEditing(action === 'edit')
      setFormData({
        question: faq.question,
        answer: faq.answer,
        category: faq.category || '',
        order: faq.order,
        isActive: faq.isActive
      })
    }
    setShowModal(true)
  }

  const handleSaveFAQ = async () => {
    if (!formData.question.trim() || !formData.answer.trim()) {
      toast.error('Please fill in both question and answer')
      return
    }

    setIsSubmitting(true)
    try {
      if (isEditing && selectedFAQ) {
        await faqService.updateFAQ(selectedFAQ._id, formData as UpdateFAQData)
        toast.success('FAQ updated successfully')
      } else {
        await faqService.createFAQ(formData)
        toast.success('FAQ created successfully')
      }
      setShowModal(false)
      fetchFAQs()
    } catch (error: any) {
      console.error('Error saving FAQ:', error)
      toast.error(error.message || 'Failed to save FAQ')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteFAQ = async (faq: FAQ) => {
    if (!window.confirm(`Are you sure you want to delete this FAQ: "${faq.question}"?`)) {
      return
    }

    try {
      await faqService.deleteFAQ(faq._id)
      toast.success('FAQ deleted successfully')
      fetchFAQs()
    } catch (error: any) {
      console.error('Error deleting FAQ:', error)
      toast.error(error.message || 'Failed to delete FAQ')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">FAQs</h1>
          <p className="text-gray-600">Manage frequently asked questions</p>
        </div>
        <button
          onClick={() => handleFAQAction('create')}
          className="btn btn-primary btn-md flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Add FAQ</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Total FAQs', value: faqs.length, color: 'bg-blue-500' },
          { label: 'Active', value: faqs.filter(f => f.isActive).length, color: 'bg-green-500' },
          { label: 'Categories', value: categories.length - 1, color: 'bg-purple-500' },
          { label: 'Inactive', value: faqs.filter(f => !f.isActive).length, color: 'bg-gray-500' }
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200"
          >
            <div className="">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900 mb-2">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-lg ${stat.color}`}>
                  <HelpCircle className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
        <div className="">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search FAQs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 pl-10 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all duration-200 bg-gray-50"
              />
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full sm:w-48 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all duration-200 bg-gray-50"
            >
              {categories.map(category => (
                <option key={category} value={category}>
                  {category === 'ALL' ? 'All Categories' : category}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* FAQs List */}
      <div className="space-y-4">
        {filteredFAQs.map((faq, index) => (
          <motion.div
            key={faq._id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200"
          >
            <div className="">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="text-lg font-medium text-gray-900">
                      {faq.question}
                    </h3>
                    {faq.category && (
                      <span className="badge badge-info text-xs">
                        {faq.category}
                      </span>
                    )}
                    <span className={`badge text-xs ${
                      faq.isActive ? 'badge-success' : 'badge-danger'
                    }`}>
                      {faq.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                  {expandedItems.has(faq._id) && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-3"
                    >
                      <p className="text-ivory-700 bg-gray-50 p-4 rounded-lg">
                        {faq.answer}
                      </p>
                    </motion.div>
                  )}
                </div>
                
                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => toggleExpanded(faq._id)}
                    className="p-2 text-gray-600 hover:text-primary-600 transition-colors"
                    title={expandedItems.has(faq._id) ? 'Collapse' : 'Expand'}
                  >
                    {expandedItems.has(faq._id) ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={() => handleFAQAction('view', faq)}
                    className="p-2 text-gray-600 hover:text-primary-600 transition-colors"
                    title="View Details"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleFAQAction('edit', faq)}
                    className="p-2 text-gray-600 hover:text-primary-600 transition-colors"
                    title="Edit FAQ"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteFAQ(faq)}
                    className="p-2 text-gray-600 hover:text-red-600 transition-colors"
                    title="Delete FAQ"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* FAQ Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <h3 className="text-xl font-semibold text-gray-900">
                  {isEditing ? 'Edit FAQ' : selectedFAQ ? 'FAQ Details' : 'Add New FAQ'}
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-ivory-700 mb-2">
                  Question *
                </label>
                <input
                  type="text"
                  value={formData.question}
                  onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                  className="input w-full"
                  placeholder="Enter the question..."
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-ivory-700 mb-2">
                  Answer *
                </label>
                <textarea
                  value={formData.answer}
                  onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                  className="input w-full h-32"
                  placeholder="Enter the answer..."
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-ivory-700 mb-2">
                    Category
                  </label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="input w-full"
                    placeholder="Enter category..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-ivory-700 mb-2">
                    Order
                  </label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    className="input w-full"
                    placeholder="Enter order..."
                    min="0"
                  />
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="isActive" className="text-sm text-ivory-700">
                  Active
                </label>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className="btn btn-secondary btn-md"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveFAQ}
                className="btn btn-primary btn-md"
                disabled={isSubmitting}
              >
                {isSubmitting 
                  ? (isEditing ? 'Updating...' : 'Creating...') 
                  : (isEditing ? 'Update FAQ' : 'Create FAQ')}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default FAQs


